// src/features/users/components/UserEdit.tsx

import { useState, useMemo } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  required,
  minLength,
  maxLength,
  email,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  useUpdate,
  useRefresh,
  usePermissions,
} from 'react-admin';
import type { UserRole } from '@/types/models';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  GroupWork as GroupWorkIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Grid,
  Box,
  TextField,
  Backdrop,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';
import { AddGroupHotelsButton } from './AddGroupHotelsButton';
import { useWatch, useFormState } from 'react-hook-form';
import { useUserPermissions } from '../hooks/useUserPermissions';
import type { User } from '../types';
import { getAssignableRoles, roleNeedsHotels } from '../roles';

/**
 * Validation rules for user editing
 */
const validateEmail = [
  required('resources.users.validation.email_required'),
  email('resources.users.validation.email_invalid'),
];

const validateFirstName = [
  required('resources.users.validation.firstName_required'),
  minLength(2, 'resources.users.validation.name_min_length'),
  maxLength(100, 'resources.users.validation.name_max_length'),
];

const validateLastName = [
  required('resources.users.validation.lastName_required'),
  minLength(2, 'resources.users.validation.name_min_length'),
  maxLength(100, 'resources.users.validation.name_max_length'),
];

const validateRole = [
  required('resources.users.validation.role_required'),
];

/**
 * Form-level validation: ensures required assignments are present based on role
 */
const validateUserForm = (values: any) => {
  const errors: Record<string, string> = {};
  const role = values.role;

  if (roleNeedsHotels(role) &&
      (!values.assignedHotels || values.assignedHotels.length === 0)) {
    errors.assignedHotels = 'resources.users.validation.hotels_required_for_role';
  }

  if (role === 'GroupAdmin' &&
      (!values.assignedGroups || values.assignedGroups.length === 0)) {
    errors.assignedGroups = 'resources.users.validation.groups_required_for_role';
  }

  return errors;
};

/**
 * Password Reset Dialog Component
 * Allows admins to set a temporary password for a user
 */
interface PasswordDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
}

const PasswordResetDialog: React.FC<PasswordDialogProps> = ({ open, onClose, userId }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!newPassword) {
      newErrors.newPassword = translate('resources.users.validation.password_required');
    } else if (newPassword.length < 8) {
      newErrors.newPassword = translate('resources.users.validation.password_min_length');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = translate('resources.users.validation.confirm_password_required');
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = translate('resources.users.validation.passwords_not_match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await update(
        'users',
        {
          id: `${userId}/password`,
          data: {
            newPassword,
          },
          previousData: undefined,
        },
        {
          onSuccess: () => {
            notify('resources.users.messages.password_reset_success', { type: 'success' });
            handleClose();
            refresh();
          },
          onError: (error: any) => {
            let errorMessage = translate('resources.users.messages.password_reset_error');

            if (error?.body?.error?.message) {
              errorMessage = error.body.error.message;
            }

            notify(errorMessage, { type: 'error' });
          },
        }
      );
    } catch (error) {
      // Error handled in onError callback
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('resources.users.sections.reset_password')}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('resources.users.messages.temporary_password_info')}
        </Alert>

        <TextField
          label={translate('resources.users.fields.newPassword')}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          error={!!errors.newPassword}
          helperText={errors.newPassword}
          sx={{ mb: 2 }}
        />

        <TextField
          label={translate('resources.users.fields.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          autoFocus
        >
          {isLoading
            ? translate('ra.action.loading')
            : translate('resources.users.actions.reset_password')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


/**
 * Basic info section with password reset button
 */
const BasicInfoSection = () => {
  const translate = useTranslate();
  const record = useRecordContext<User>();
  const { canManagePasswords, canManageSuperAdmins } = useUserPermissions();
  const { permissions } = usePermissions();
  const currentRole = permissions as UserRole | undefined;
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  // If editing a SuperAdmin and user can't manage SuperAdmins, disable role change
  const isTargetSuperAdmin = record?.role === 'SuperAdmin';
  const roleDisabled = isTargetSuperAdmin && !canManageSuperAdmins;

  // Get assignable roles based on current user's role
  const assignableRoles = useMemo(() => getAssignableRoles(currentRole, canManageSuperAdmins), [currentRole, canManageSuperAdmins]);

  // Role choices - only show roles the current user can assign
  const roleChoices = useMemo(() =>
    assignableRoles.map((role) => ({
      id: role,
      name: translate(`resources.users.roles.${role.charAt(0).toLowerCase() + role.slice(1)}`),
    })),
    [assignableRoles, translate]
  );

  return (
    <>
      <MyFormSection
        title={translate('resources.users.edit.title')}
        subtitle={translate('resources.users.edit.subtitle')}
        icon={<PersonIcon />}
        variant="outlined"
      >
        {/* First Name */}
        <TextInput
          source="firstName"
          label={translate('resources.users.fields.firstName')}
          validate={validateFirstName}
          fullWidth
        />

        {/* Last Name */}
        <TextInput
          source="lastName"
          label={translate('resources.users.fields.lastName')}
          validate={validateLastName}
          fullWidth
        />

        {/* Email */}
        <TextInput
          source="email"
          label={translate('resources.users.fields.email')}
          validate={validateEmail}
          fullWidth
          type="email"
        />

        {/* Role */}
        <SelectInput
          source="role"
          label={translate('resources.users.fields.role')}
          choices={roleChoices}
          validate={validateRole}
          fullWidth
          disabled={roleDisabled}
          helperText={roleDisabled
            ? translate('resources.users.messages.superadmin_management_denied')
            : translate('resources.users.validation.role_helper')
          }
        />

        {/* Password Reset Button */}
        {canManagePasswords && record && (
          <Button
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={() => setPasswordDialogOpen(true)}
            fullWidth
            sx={{ mt: 1 }}
          >
            {translate('resources.users.actions.reset_password')}
          </Button>
        )}
      </MyFormSection>

      {/* Password Reset Dialog */}
      {canManagePasswords && record && (
        <PasswordResetDialog
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          userId={record.id}
        />
      )}
    </>
  );
};

/**
 * Custom toolbar
 */
const EditToolbar = () => {
  const { canDelete, canManageSuperAdmins } = useUserPermissions();
  const record = useRecordContext<User>();
  const isTargetSuperAdmin = record?.role === 'SuperAdmin';
  const showDelete = canDelete && !(isTargetSuperAdmin && !canManageSuperAdmins);

  return (
    <MyToolbar
      resource="users"
      showDelete={showDelete}
      deleteButton={showDelete}
    />
  );
};

/**
 * Form content with responsive grid layout.
 * Shows basic info and assignments side-by-side on desktop.
 */
const EditFormContent = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const role = useWatch({ name: 'role' });
  const { isSubmitting } = useFormState();

  const canAssignGroups = role === 'GroupAdmin';
  const canAssignHotels = roleNeedsHotels(role);
  const hasFullAccess = role === 'SuperAdmin' || role === 'Admin';
  const showAssignments = hasFullAccess || canAssignGroups || canAssignHotels;

  return (
    <MyFormContainer maxWidth="lg">
      {isSubmitting && (
        <Backdrop
          open
          sx={{
            position: 'absolute',
            zIndex: 1,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, color: 'text.secondary' }}>
            {translate('resources.users.messages.saving_user')}
          </Typography>
        </Backdrop>
      )}
      <Box sx={{
        maxWidth: showAssignments ? '1000px' : '600px',
        transition: 'max-width 0.3s ease',
        mx: 'auto',
        width: '100%',
        py: { xs: 2, md: 3 },
      }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={showAssignments ? 7 : 12}>
            <BasicInfoSection />
          </Grid>

          {showAssignments && (
            <Grid item xs={12} md={5}>
              {hasFullAccess ? (
                <MyFormSection
                  title={translate('resources.users.sections.assignments')}
                  subtitle={translate('resources.users.fields.allAccess')}
                  icon={<GroupWorkIcon />}
                  variant="outlined"
                >
                  <Alert severity="info">
                    {translate('resources.users.messages.superadmin_access')}
                  </Alert>
                </MyFormSection>
              ) : (
                <MyFormSection
                  title={translate('resources.users.sections.assignments')}
                  subtitle={translate('resources.users.sections.assignments_subtitle')}
                  icon={<GroupWorkIcon />}
                  variant="outlined"
                >
                  {canAssignGroups && (
                    <ReferenceArrayInput
                      source="assignedGroups"
                      reference="groups"
                      sort={{ field: 'name', order: 'ASC' }}
                    >
                      <SelectArrayInput
                        label={translate('resources.users.fields.assignedGroups')}
                        optionText="name"
                        fullWidth
                        helperText={translate('resources.users.validation.groups_helper')}
                      />
                    </ReferenceArrayInput>
                  )}

                  {canAssignHotels && (
                    <>
                      <AddGroupHotelsButton />
                      <ReferenceArrayInput
                        source="assignedHotels"
                        reference="hotels"
                        sort={{ field: 'name', order: 'ASC' }}
                      >
                        <SelectArrayInput
                          label={translate('resources.users.fields.assignedHotels')}
                          optionText="name"
                          fullWidth
                          helperText={translate('resources.users.validation.hotels_helper')}
                        />
                      </ReferenceArrayInput>
                    </>
                  )}
                </MyFormSection>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    </MyFormContainer>
  );
};

/**
 * User edit component
 */
export const UserEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = () => {
    notify('resources.users.messages.update_success', { type: 'success' });
    redirect('list', 'users');
  };

  const onError = (error: any) => {
    const errorCode = error?.body?.error?.code;
    let errorMessage = translate('resources.users.messages.update_error');

    // Handle specific error codes
    if (errorCode === 'EMAIL_ALREADY_EXISTS') {
      errorMessage = translate('resources.users.messages.email_already_exists');
    } else if (errorCode === 'USER_NOT_FOUND') {
      errorMessage = translate('resources.users.messages.user_not_found');
    } else if (errorCode === 'SELF_ROLE_CHANGE') {
      errorMessage = translate('resources.users.messages.self_role_change');
    } else if (errorCode === 'SUPERADMIN_MANAGEMENT_DENIED') {
      errorMessage = translate('resources.users.messages.superadmin_management_denied');
    } else if (errorCode === 'MISSING_HOTEL_ASSIGNMENT') {
      errorMessage = translate('resources.users.validation.hotels_required_for_role');
    } else if (errorCode === 'MISSING_GROUP_ASSIGNMENT') {
      errorMessage = translate('resources.users.validation.groups_required_for_role');
    } else if (errorCode === 'USER_DEACTIVATED') {
      errorMessage = translate('resources.users.messages.user_deactivated');
    }

    notify(errorMessage, { type: 'error' });
  };

  return (
    <Edit
      mutationOptions={{ onSuccess, onError }}
      mutationMode="pessimistic"
      title={translate('resources.users.edit.title')}
    >
      <SimpleForm toolbar={<EditToolbar />} validate={validateUserForm}>
        <EditFormContent />
      </SimpleForm>
    </Edit>
  );
};
